import { type ChangeEvent, type FC, useEffect, useRef } from 'react';
import { ListFilter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslations } from 'next-intl';

export const FilterBar: FC<{
  queryString?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  showFilterInput: boolean;
}> = ({ queryString, onChange, showFilterInput }) => {
  const tCommon = useTranslations('common');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input as soon as the filter opens so the user can type immediately.
  useEffect(() => {
    if (showFilterInput) {
      inputRef.current?.focus();
    }
  }, [showFilterInput]);

  if (!showFilterInput) {
    return null;
  }

  const clearSearch = () => {
    // Commit the empty value through the existing onChange (updates the store),
    // and clear the uncontrolled input's DOM value so it empties immediately.
    onChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className="flex h-8 items-center gap-x-2 bg-input-background px-2">
      <ListFilter data-cy="gallery-search-filter-icon" className="icon-5 stroke-icon-base" />
      <div className="text-sm font-bold">{tCommon('filterLabel')}</div>
      <Input
        safeInputRef={inputRef}
        type="search"
        defaultValue={queryString}
        placeholder={tCommon('filterPlaceholder')}
        onChange={onChange}
        className="z-20 max-h-full min-h-full w-full rounded-none border-0 bg-transparent p-0 shadow-none ring-transparent focus-within:ring-0 focus-within:ring-transparent focus-within:ring-offset-0 focus-within:outline-hidden focus-within:duration-0 [&_input::-webkit-search-cancel-button]:appearance-none"
        {...(queryString && {
          appendIcon: (
            <Button
              type="button"
              variant="main-toolbar"
              onClick={clearSearch}
              aria-label={tCommon('clearSearch')}
              data-cy="filter-clear-button"
              className="icon-5 p-0"
              iconClassName="stroke-icon-base icon-4"
              icon={X}
            />
          ),
        })}
      />
    </div>
  );
};
